import { motion, AnimatePresence } from 'framer-motion'
import { ExportSquare } from 'iconsax-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import FacebookIcon from '@/assets/socialMedia/Facebook.svg?react'
import InstagramIcon from '@/assets/socialMedia/Instagram.svg?react'
import LinkedInIcon from '@/assets/socialMedia/LinkedIn.svg?react'
import PinterestIcon from '@/assets/socialMedia/Pinterest.svg?react'
import RedditIcon from '@/assets/socialMedia/Reddit.svg?react'
import TikTokIcon from '@/assets/socialMedia/TikTok.svg?react'
import XIcon from '@/assets/socialMedia/X.svg?react'
import type { ISocialMediaFound } from '@/hooks/useReportData'

const PLATFORM_ICONS: Record<string, any> = {
  Facebook: FacebookIcon,
  Instagram: InstagramIcon,
  LinkedIn: LinkedInIcon,
  Pinterest: PinterestIcon,
  Reddit: RedditIcon,
  TikTok: TikTokIcon,
  Twitter: XIcon,
}

function ProfileAvatar({ sm }: { sm: ISocialMediaFound }) {
  const Icon = PLATFORM_ICONS[sm.platform]
  const [imgError, setImgError] = useState(false)
  const hasImg = sm.profileImgUrl && !imgError

  return (
    <div className="relative flex-shrink-0">
      {hasImg ? (
        <img
          src={sm.profileImgUrl}
          alt={sm.userName}
          className="w-10 h-10 rounded-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        <div className="w-10 h-10 rounded-full bg-neutralColor30/30 flex items-center justify-center">
          {Icon ? (
            <Icon width={18} height={18} className="text-neutralColor" />
          ) : (
            <span className="text-xs text-neutralColor60">●</span>
          )}
        </div>
      )}
      {hasImg && Icon && (
        <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-baseColorHighlight border border-neutralColor5 flex items-center justify-center">
          <Icon width={10} height={10} className="text-neutralColor" />
        </div>
      )}
    </div>
  )
}

interface SocialMediaListProps {
  socialMedia: ISocialMediaFound[]
  inputProfiles: ISocialMediaFound[]
}

export function SocialMediaList({
  socialMedia,
  inputProfiles,
}: SocialMediaListProps) {
  const { t } = useTranslation()
  const scrapedPlatforms = new Set(socialMedia.map((s) => s.platform))
  const pendingInputs = inputProfiles.filter(
    (i) => !scrapedPlatforms.has(i.platform),
  )
  const items = [...socialMedia, ...pendingInputs]

  return (
    <div className="space-y-3">
      <h3 className="text-xl font-medium text-neutralColor">
        {t('dashboard.newSearch.generatingReport.subject.socialMediaPresence')}
      </h3>

      <div>
        <AnimatePresence initial={false}>
          {items.map((sm, i) => {
            const isClickable = !!sm.url

            const row = (
              <motion.div
                key={`${sm.platform}-${sm.userName}`}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05, duration: 0.4, ease: 'easeOut' }}
                className={
                  i > 0
                    ? 'flex items-start gap-3 py-3 border-t border-neutralColor5'
                    : 'flex items-start gap-3 py-3'
                }
              >
                <ProfileAvatar sm={sm} />

                <div className="flex-1 min-w-0">
                  <span className="text-sm text-neutralColor">
                    {sm.userName || sm.platform}
                  </span>
                  {sm.bio && (
                    <p className="text-xs text-neutralColor60 leading-relaxed mt-0.5 line-clamp-2">
                      {sm.bio}
                    </p>
                  )}
                </div>

                {isClickable && (
                  <ExportSquare
                    size={16}
                    className="text-neutralColor60 flex-shrink-0 mt-0.5"
                  />
                )}
              </motion.div>
            )

            if (isClickable) {
              return (
                <a
                  key={`${sm.platform}-${sm.userName}`}
                  href={sm.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block hover:bg-neutralColor5/50 rounded-lg transition-colors -mx-2 px-2"
                >
                  {row}
                </a>
              )
            }

            return row
          })}
        </AnimatePresence>
      </div>
    </div>
  )
}
